import * as THREE from 'three';
import { disposeObject } from '../helpers/ObjectHelpers';
import { Measurement } from '../Measurement';
import {
  get,
  getExists,
  getInt,
  getNumber,
  buildCoupledFrame,
  getOrNull,
  getObjectNumbersInThousands,
  sumObjects,
  subtractObjects,
  createConvexSolid2DGeometry,
  createConvexSolidGeometry,
  createConvexBorderGeometry,
  setFrontMaterial,
  setBackMaterial,
  setOutsideMaterial,
  setInsideMaterial,
  nextMullionId,
  getObjectNumbersInThousandsOrNull,
  createVentilationFillGeometry,
  setMaterial,
  multiplyObjectScalar,
  getBool,
  getNumberOrNull,
  getBoolOrNull,
  getColorOrNull,
} from './WindowHelpers';
import { Mullion } from './Mullion';
import * as util from '../util';
import { RectangularWindow } from './RectangularWindow';
import { combineGeometries } from '../helpers/GeometryHelpers';
import {
  getObjectWorldPosition,
  vectorToCoords,
} from '../helpers/WorldHelpers';
import { keyCodes } from '../handlers/KeyboardHandler';
import {
  createBottomHungInwardOpeningMarker,
  createFixedFrameOpeningMarker,
  createLeftHungInwardOpeningMarker,
  createLeftHungKippDrehInwardOpeningMarker,
  createLeftHungOutwardOpeningMarker,
  createPushLeftOpeningMarker,
  createPushRightOpeningMarker,
  createRightHungInwardOpeningMarker,
  createRightHungKippDrehInwardOpeningMarker,
  createRightHungOutwardOpeningMarker,
  createTopHungOutwardOpeningMarker,
  createTwistWindowOpeningMarker,
} from '../helpers/OpeningMarkers';
import { InteractionMode } from '../handlers/InteractionHandler';
import {
  buildBlind,
  buildHinging,
  buildMosquitoNet,
  buildPlisse,
} from '../helpers/AccessoriesHelpers';
import { INPUT_EDGE_MARGIN } from '../handlers/InputHandler';
import { clampAngle } from '../helpers/AngleHelpers';
import { topologicalSort } from '../helpers/SortHelpers';
import { groupBy } from 'lodash';
import { createTextMesh } from '../MeshHelpers';
import { parseNumberString, roundNumber } from '../helpers/NumberHelpers';

var STATE_OPENING = 0x1;
var STATE_CLOSING = 0x2;
var STATE_FLIPPING = 0x4;

var MULLION_HOVER_MATERIAL = new THREE.MeshBasicMaterial({
  color: 0xffe032,
});
var MULLION_SELECT_MATERIAL = new THREE.MeshBasicMaterial({
  color: 0xd34030,
});
var MULLION_HIGHLIGHT_MATERIAL = new THREE.MeshBasicMaterial({
  color: 0x70e9ff,
});
var SPACE_HIGHLIGHT_MATERIAL = new THREE.LineBasicMaterial({
  color: 0x00870f,
  opacity: 0.5,
  transparent: true,
  polygonOffset: true,
  polygonOffsetUnits: -3,
  polygonOffsetFactor: -1,
});

var MESH_HIGHLIGHT_MATERIAL = new THREE.MeshBasicMaterial({
  color: 0x70e9ff,
  transparent: true,
  opacity: 0.5,
  polygonOffset: true,
  polygonOffsetFactor: -1,
  polygonOffsetUnits: -3,
});

const USE_WIREFRAME = false;

function buildMove(
  container,
  def,
  data,
  dLeft,
  dRight,
  dTop,
  dBottom,
  dFront,
  dBack,
  nextFunc
) {
  const backPoint = dBack / data.b;
  const frontPoint = (data.b - dFront) / data.b;
  const outerBorderAdjust = multiplyObjectScalar(
    subtractObjects(data.innerBorderAdjust, data.outerBorderAdjust),
    backPoint
  );
  const innerBorderAdjust = multiplyObjectScalar(
    subtractObjects(data.innerBorderAdjust, data.outerBorderAdjust),
    frontPoint
  );

  dTop -= outerBorderAdjust.OVP || 0;
  dBottom -= outerBorderAdjust.UNP || 0;
  dLeft -= outerBorderAdjust.VSP || 0;
  dRight -= outerBorderAdjust.HSP || 0;

  const newData = Object.assign({}, data);
  newData.left += dLeft;
  newData.right -= dRight;
  newData.top += dTop;
  newData.bottom -= dBottom;
  newData.w = newData.right - newData.left;
  newData.h = newData.bottom - newData.top;
  newData.b -= dFront + dBack;
  newData.innerBorderAdjust = subtractObjects(
    innerBorderAdjust,
    outerBorderAdjust
  );
  newData.outerBorderAdjust = subtractObjects(
    outerBorderAdjust,
    outerBorderAdjust
  );

  const movedContainer = new THREE.Object3D();
  movedContainer.position.x += (dLeft - dRight) / 2;
  movedContainer.position.y += (dBottom - dTop) / 2;
  movedContainer.position.z += (dFront - dBack) / 2;
  container.add(movedContainer);

  nextFunc.call(this, movedContainer, def, newData);
}

function buildObject(container, def, data, innerWidths, outerWidths, nextFunc) {
  const outerRectWidths = this._transformDimensionsToRect(
    outerWidths,
    data.w,
    data.h,
    data.geometryData
  );

  const childContainer = new THREE.Object3D();
  childContainer.position.x +=
    (outerRectWidths.left - outerRectWidths.right) / 2;
  childContainer.position.y +=
    (outerRectWidths.bottom - outerRectWidths.top) / 2;
  container.add(childContainer);

  const newData = Object.assign({}, data);
  newData.left += outerRectWidths.left;
  newData.right -= outerRectWidths.right;
  newData.top += outerRectWidths.top;
  newData.bottom -= outerRectWidths.bottom;
  newData.w -= outerRectWidths.left + outerRectWidths.right;
  newData.h -= outerRectWidths.top + outerRectWidths.bottom;
  newData.geometryData = sumObjects(
    newData.geometryData,
    this._getGeometryChangeOnShrink(
      newData.geometryData,
      outerWidths,
      data.w,
      data.h
    )
  );

  Object.keys(data.innerBorderAdjust).forEach(key => {
    newData.innerBorderAdjust[key] += outerWidths[key] - innerWidths[key];
  });

  nextFunc.call(this, childContainer, def, newData);
}

function getFixedSashAdjustment(def, dz, breadth, d) {
  var outer = {};
  var inner = {};
  Object.keys(d.outerBorderAdjust).forEach(key => {
    if (
      this.isRectangular &&
      ((key === 'VSP' && get(def, 'Leftsymbol') !== 'VSP.') ||
        (key === 'HSP' && get(def, 'Rightsymbol') !== 'HSP.') ||
        (key === 'OVP' && get(def, 'Uppersymbol') !== 'OVP.') ||
        (key === 'UNP' && get(def, 'Lowersymbol') !== 'UNP.'))
    ) {
      outer[key] = 0;
      inner[key] = 0;
    } else {
      var slope = (d.innerBorderAdjust[key] - d.outerBorderAdjust[key]) / d.b;
      outer[key] = d.outerBorderAdjust[key] + slope * dz;
      inner[key] = d.outerBorderAdjust[key] + slope * (dz + breadth);
    }
  });
  return { outer, inner };
}

function buildSashClearance(def, container, d) {
  def = get(def, 'TMDFSClearances');

  var w, h, x, y;
  if (this.isRectangular) {
    var symbols = d.symbols;
    var top = symbols[get(def, 'Uppersymbol')];
    var bottom = symbols[get(def, 'Lowersymbol')];
    var left = symbols[get(def, 'Leftsymbol')];
    var right = symbols[get(def, 'Rightsymbol')];

    w = right.pos - left.pos - (right.width + left.width) / 2;
    h = top.pos - bottom.pos - (top.width + bottom.width) / 2;
    x = (right.pos - right.width / 2 + (left.pos + left.width / 2)) / 2;
    y = (top.pos - top.width / 2 + (bottom.pos + bottom.width / 2)) / 2;

    if (
      (def.PureGlassField === 'True' || def.PureGlassField === true) &&
      def.Glass &&
      def.Glass.Width
    ) {
      def.Glass.Width = Math.round(w * 1000000) / 1000; // Up to 3 decimal places
      def.Glass.Height = Math.round(h * 1000000) / 1000; // Up to 3 decimal places
    }

    if (def.Accessories) {
      const accessoriesConatiner = new THREE.Object3D();
      accessoriesConatiner.position.x += x;
      accessoriesConatiner.position.y += y;
      container.add(accessoriesConatiner);
      const oldW = d.w;
      const oldH = d.h;
      d.w = w;
      d.h = h;
      util.forEachIfArray(
        this,
        buildAccessories,
        def.Accessories,
        accessoriesConatiner,
        d
      );
      d.w = oldW;
      d.h = oldH;
    }
  } else {
    w = d.w;
    h = d.h;
    x = 0;
    y = 0;
  }

  if (def.IsHighlighted === true) {
    var adjust = getFixedSashAdjustment.call(this, def, 0, d.b, d);
    buildHighlightedSpace.call(
      this,
      container,
      x,
      y,
      d.b / 2,
      w,
      h,
      d.b,
      adjust.outer,
      d.geometryData
    );
  }

  if (def.Glass && get(def, 'Glass', 'Type')) {
    var moveGlass = 0;
    if (def.Glass.Position && get(def, 'Glass', 'Position')) {
      moveGlass = getNumber(def, 'Glass', 'Position') / 1000;
    }

    var glassBreadth = d.b;
    if (def.Glass.Depth) {
      glassBreadth = getNumber(def, 'Glass', 'Depth') / 1000;
    }

    var adjust = getFixedSashAdjustment.call(
      this,
      def,
      moveGlass,
      glassBreadth,
      d
    );

    var geo = this._createSolidGeometry(
      w,
      h,
      glassBreadth,
      adjust.inner,
      adjust.outer,
      d.geometryData
    );

    var type = get(def, 'Glass', 'Type');
    var material;
    if (type === 'G') {
      var color = /*isHex(key) ? hexToNumber(key) :*/ 0x9eeaff;
      material = new THREE.MeshLambertMaterial({
        color: color,
        transparent: true,
        opacity: 0.3,
      });
    } else if (type === 'F' || type === 'V') {
      material = [d.treeMaterial, d.coupledFrameMaterial, d.whiteMaterial];
    } else {
      throw 'Unknown glass type';
    }
    var mesh = new THREE.Mesh(geo, material);
    mesh.position.x = x;
    mesh.position.y = y;
    mesh.position.z += d.b / 2;
    mesh.position.z -= glassBreadth / 2;
    mesh.position.z -= moveGlass;

    if (type === 'F' || type === 'V') {
      if (d.coupledFrameMaterial) {
        setBackMaterial(mesh, 1);
      }
      mesh.buildEdges();
    }
    if (type === 'V') {
      const ventilationGeometry = createVentilationFillGeometry(
        w,
        h,
        glassBreadth / 2
      );
      if (d.coupledFrameMaterial) {
        for (const face of ventilationGeometry.faces) {
          face.materialIndex = 1;
        }
      }
      geo = combineGeometries(geo, ventilationGeometry);
      material = d.treeMaterial.clone();
      material.side = THREE.DoubleSide;
      const ventilationMesh = new THREE.Mesh(geo, [
        material,
        d.coupledFrameMaterial,
      ]);
      ventilationMesh.buildEdges(1.0, 90);
      mesh.add(ventilationMesh);
      if (d.coupledFrameMaterial) {
        setBackMaterial(ventilationMesh, 1);
      }
    }
    container.add(mesh);
  }
}

function prepareProfiles(def, d) {
  // Precalculations for total width among mullions
  // between the same StartSymbol and EndSymbol.
  // Used for SplitNumerator and SplitDenominator.

  const unsortedDefs = [];
  util.forEachIfArray(
    this,
    function (def) {
      unsortedDefs.push(get(def, 'TMDFSProfiles'));
    },
    def
  );

  const defs = topologicalSort(
    unsortedDefs,
    def => get(def, 'Symbol'),
    def => [
      get(def, 'SplitFrom'),
      get(def, 'SplitTo'),
      get(def, 'StartSymbol'),
      get(def, 'EndSymbol'),
    ]
  );

  const groupedDefs = Object.values(
    groupBy(
      defs,
      def =>
        `${get(def, 'StartSymbol')};${get(def, 'EndSymbol')};${get(
          def,
          'ProfileCategory'
        )}`
    )
  ).map(group => {
    const symbols = new Set(group.map(def => get(def, 'Symbol')));
    const splitFroms = group.filter(def => !symbols.has(get(def, 'SplitFrom')));
    const splitTos = group.filter(def => !symbols.has(get(def, 'SplitTo')));
    return {
      widthLeft: group.reduce(
        (acc, def) => acc + getNumber(def, 'Width') / 1000,
        0
      ),
      defs: group,
      startSymbol: get(group[0], 'StartSymbol'),
      endSymbol: get(group[0], 'EndSymbol'),
      splitFromSymbol: get(splitFroms[0], 'SplitFrom'),
      splitToSymbol: get(splitTos[0], 'SplitTo'),
    };
  });
  return groupedDefs;
}

function buildProfile(batches, batch, defIndex, container, d) {
  const def = batch.defs[defIndex];

  var symbols = d.symbols;
  var dss = get(def, 'StartSymbol');
  var des = get(def, 'EndSymbol');
  var profileCategory = get(def, 'ProfileCategory');

  var currSymbol = get(def, 'Symbol');
  var ss = symbols[dss]; //Start symbol
  var es = symbols[des]; //End symbol
  var len = es.pos - ss.pos - (ss.width + es.width) / 2;

  var axis = symbols[dss].axis === 'x' ? 'y' : 'x';

  const batchWidthLeft = batch.widthLeft;
  const getPosition = percentage => {
    const fromSymbol = get(def, 'SplitFrom');
    const toSymbol = get(def, 'SplitTo');
    const from = symbols[fromSymbol];
    const to = symbols[toSymbol];
    const axDir = axis === 'y' ? -1 : 1;
    const mulWidth = getNumber(def, 'Width') / 1000;
    const totalGlassLength =
      to.absPos -
      from.absPos -
      ((to.width + from.width) / 2) * axDir -
      batchWidthLeft * axDir;
    const glassLengthBefore = totalGlassLength * percentage;
    const mulPos =
      from.absPos +
      (from.width / 2) * axDir +
      glassLengthBefore +
      (mulWidth / 2) * axDir;
    return mulPos;
  };

  var pos;
  if (def.Position) {
    pos = getNumber(def, 'Position') / 1000;
  } else {
    const numerator = getNumber(def, 'SplitNumerator');
    const denominator = getNumber(def, 'SplitDenominator');
    pos = getPosition(numerator / denominator);
  }
  var absPos = pos;
  if (axis === 'x') {
    pos = absPos - d.left - d.w / 2;
  } else {
    pos = d.h / 2 + d.top - absPos;
  }

  var pb = d.b;
  if (def.Depth) {
    pb = getNumber(def, 'Depth') / 1000;
  }
  const zOffset = (getNumberOrNull(def, 'Offset') || 0) / 1000;

  var ls = {
    //Line symbol
    name: currSymbol,
    category: profileCategory,
    depth: pb,
    pos: pos,
    absPos: absPos,
    axis: axis,
    width: getNumber(def, 'Width') / 1000,
  };
  symbols[currSymbol] = ls;

  var pw = axis == 'x' ? ls.width : len;
  var ph = axis == 'x' ? len : ls.width;

  var outerAdjust = {};
  var innerAdjust = {};
  Object.keys(d.outerBorderAdjust).forEach(key => {
    if (
      (key === 'VSP' && dss !== 'VSP.') ||
      (key === 'HSP' && des !== 'HSP.') ||
      (key === 'OVP' && des !== 'OVP.') ||
      (key === 'UNP' && dss !== 'UNP.')
    ) {
      outerAdjust[key] = 0;
      innerAdjust[key] = 0;
    } else {
      var slope = (d.innerBorderAdjust[key] - d.outerBorderAdjust[key]) / d.b;
      outerAdjust[key] = d.outerBorderAdjust[key] + slope * zOffset;
      innerAdjust[key] = d.outerBorderAdjust[key] + slope * (pb + zOffset);
    }
  });

  const mullionColor = getColorOrNull(def, 'Color');
  const mullionMaterial1 =
    mullionColor === null
      ? undefined
      : new THREE.MeshLambertMaterial({
          color: mullionColor,
        });
  const mullionMaterial2 =
    mullionColor === null
      ? undefined
      : new THREE.MeshPhongMaterial({
          color: mullionColor,
          shininess: 50,
        });

  var geo = this._createMullionGeometry(
    pw,
    ph,
    pb,
    innerAdjust,
    outerAdjust,
    d.geometryData
  );
  const meshMaterial =
    mullionMaterial1 && mullionMaterial2
      ? [mullionMaterial1, mullionMaterial2, mullionMaterial1]
      : [d.treeMaterial, d.coupledFrameMaterial, d.whiteMaterial];
  var mesh = new Mullion(this._renderer, this, geo, meshMaterial);

  if (axis == 'x') {
    mesh.position.x = ls.pos;
    mesh.position.y = (ss.pos + es.pos) / 2 - (es.width - ss.width) / 4;
  } else {
    mesh.position.x = (ss.pos + es.pos) / 2 - (es.width - ss.width) / 4;
    mesh.position.y = ls.pos;
  }
  mesh.position.z += (d.b - pb) / 2 - zOffset;

  var absolutePosX = mesh.position.x + d.w / 2 + d.left;
  var absolutePosY = d.h / 2 - mesh.position.y + d.top;
  var absolutePosZ = mesh.position.z + d.b / 2;

  if (profileCategory === 'Standard profile') {
    if (d.coupledFrameMaterial) {
      setBackMaterial(mesh, 1);
    }
  } else if (profileCategory === 'Middle rail') {
    if (d.coupledFrameMaterial) {
      setBackMaterial(mesh, 1);
    }
  } else if (profileCategory === 'Decoration bar') {
    if (d.coupledFrameMaterial) {
      setFrontMaterial(mesh, 1);
      setBackMaterial(mesh, 1);
      setOutsideMaterial(mesh, 1);
    }
  }

  mesh.name = 'Bar';
  container.add(mesh);

  mesh.data = {
    //numerator: numerator,
    //denominator: denominator,
    window: this,
    def: def,
    axis: axis,
    id: nextMullionId(),
    startPos: axis === 'x' ? d.left : d.top,
    endPos: axis === 'x' ? d.left + d.w : d.top + d.h,
    currPos:
      axis === 'x'
        ? ls.pos + d.w / 2 + d.left
        : d.h - (ls.pos + d.h / 2) + d.top,
    deltaStartPos: axis === 'x' ? d.left : d.top,
    deltaEndPos:
      axis === 'x' ? this.width - d.left - d.w : this.height - d.top - d.h,
    w: pw,
    h: ph,
    b: pb,
    barWidth: ls.width,
    windowHeight: d.top + d.h,
    fullWindowWidth: this.width,
    fullWindowHeight: this.height,
    symbolName: currSymbol,
    connectedObjects: [],
    absolutePosX: absolutePosX,
    absolutePosY: absolutePosY,
    absolutePosZ: absolutePosZ,
    sash: {
      top: d.lastClearanceDimensions.top,
      bottom: d.lastClearanceDimensions.bottom,
      left: d.lastClearanceDimensions.left,
      right: d.lastClearanceDimensions.right,
    },
    identifier: def,
    clearanceMullions: d.clearanceMullions,
    resetPosition: () => {
      const initPos = this._initialMullionPositions.get(mesh.data.id);
      if (initPos === undefined) {
        return;
      }

      def.Position = initPos;
    },
  };
  mesh.data.oldPos = mesh.data.currPos;

  const clearanceBounds = {
    left: d.lastClearanceDimensions.left,
    top: d.lastClearanceDimensions.top,
    bottom: d.lastClearanceDimensions.bottom,
    right: d.lastClearanceDimensions.right,
  };

  // Add something small so .X5 always rounds up
  def.Position = roundNumber(mesh.data.currPos * 1000 + 1e-10, 1);

  if (this._initialMullionPositions.get(mesh.data.id) === undefined) {
    this._initialMullionPositions.set(mesh.data.id, def.Position);
  }

  d.clearanceMullions.push(mesh);

  this.allProfiles.push(mesh);

  if (def.Movable) {
    var eps = 1e-10;
    for (var i = 0; i < d.currProfiles.length; i++) {
      var mul2 = d.currProfiles[i];
      if (
        Math.abs(mesh.data.currPos - mul2.data.currPos) < eps &&
        mesh.data.axis === mul2.data.axis
      ) {
        if (
          (mesh.data.axis === 'x' &&
            Math.abs(mesh.data.absolutePosY - mul2.data.absolutePosY) <
              (mesh.data.h + mul2.data.h) / 2 - eps) ||
          (mesh.data.axis === 'y' &&
            Math.abs(mesh.data.absolutePosX - mul2.data.absolutePosX) <
              (mesh.data.w + mul2.data.w) / 2 - eps)
        ) {
          mesh.data.connectedObjects.push(mul2);
          mul2.data.connectedObjects.push(mesh);
        }
      }
    }

    this.addInteractableObject(mesh);
    this.movableProfiles.push(mesh);
    d.currProfiles.push(mesh);
    mesh.selectable = true;

    mesh.onClick = () => {
      if (mesh.isHighlighted) {
        this._renderer.interactionHandler.alignWithSelected(mesh);
        return false;
      }
      return true;
    };
    mesh.onSelect = () => {
      mesh.isSelected = true;

      const xDist = 10;

      mesh.highlightParallelMullions();

      const width = roundNumber(
        (mesh.data.endPos -
          mesh.data.startPos +
          mesh.data.deltaStartPos +
          mesh.data.deltaEndPos) *
          1000,
        1
      );

      const flipMeasurements =
        this.parent.imageEngine.flipVerticalMeasurements && axis === 'y';
      const displayPosition = roundNumber(
        flipMeasurements
          ? (this.height - mesh.data.currPos) * 1000
          : mesh.data.currPos * 1000,
        1
      );
      this._renderer.inputHandler.requestInputs({
        inputs: [
          {
            headerText: 'Position',
            appendText: `/${width}`,
            defaultValue: displayPosition,
            getCoords: inputContainer => {
              const cameraRot = clampAngle(
                this._renderer.camera.obj.rotation.y
              );
              const dir =
                cameraRot < Math.PI / 2 && cameraRot > -Math.PI / 2 ? 1 : -1;

              const meshPos = getObjectWorldPosition(mesh);
              const meshLeftPos = new THREE.Vector3((-pw / 2) * dir, 0, 0).add(
                meshPos
              );
              const meshRightPos = new THREE.Vector3((pw / 2) * dir, 0, 0).add(
                meshPos
              );
              const midCoords = vectorToCoords(
                meshPos,
                this._renderer.renderer,
                this._renderer.camera
              );
              const leftCoords = vectorToCoords(
                meshLeftPos,
                this._renderer.renderer,
                this._renderer.camera
              );
              const rightCoords = vectorToCoords(
                meshRightPos,
                this._renderer.renderer,
                this._renderer.camera
              );

              const rendererSize = this._renderer.renderer.getSize(
                new THREE.Vector2()
              );
              const fitsOnRightSide =
                rightCoords.x +
                  xDist +
                  inputContainer.width() +
                  INPUT_EDGE_MARGIN <
                rendererSize.width;
              const xPos = fitsOnRightSide
                ? rightCoords.x + xDist
                : leftCoords.x - xDist - inputContainer.width();
              const yPos = midCoords.y - inputContainer.height() / 2;
              return {
                x: xPos,
                y: yPos,
              };
            },
            onSubmit: value => {
              this._renderer.scene.updateMatrixWorld();

              const v = roundNumber(parseNumberString(value), 1) / 1000;
              const pos = flipMeasurements ? this.height - v : v;
              this._renderer.interactionHandler.moveSelectedObjects(pos);
              this._renderer.interactionHandler.clearSelectedObjects();
            },
            buttons: [
              {
                side: 'left',
                text: 'Återställ',
                onClick: () => {
                  mesh.data.resetPosition();
                  for (const connectedMullion of mesh.data.connectedObjects) {
                    connectedMullion.data.resetPosition();
                  }
                  this._renderer.facade.rebuild();
                },
              },
              {
                side: 'right',
                text: 'OK',
                onClick: options => options.submit(),
              },
            ],
          },
        ],
        onScroll: delta => {
          const dist = 0.01;
          const dir = delta > 0 ? -1 : 1;
          this._renderer.interactionHandler.moveSelectedObjectsRelatively(
            dist * dir
          );
        },
        onInput: which => {
          const dist = 0.01;
          if (which === keyCodes.arrow_left) {
            this._renderer.interactionHandler.moveSelectedObjectsRelatively(
              -dist,
              true,
              false
            );
          } else if (which === keyCodes.arrow_up) {
            this._renderer.interactionHandler.moveSelectedObjectsRelatively(
              dist,
              false,
              true
            );
          } else if (which === keyCodes.arrow_right) {
            this._renderer.interactionHandler.moveSelectedObjectsRelatively(
              dist,
              true,
              false
            );
          } else if (which === keyCodes.arrow_down) {
            this._renderer.interactionHandler.moveSelectedObjectsRelatively(
              -dist,
              false,
              true
            );
          }
        },
      });

      if (def.ProfilePreferences) {
        util.forEachIfArray(
          this,
          function (ppDef) {
            const secondaryAxisPos =
              axis === 'x' ? mesh.position.y : mesh.position.x;
            const zPos = mesh.position.z;
            const code = get(ppDef, 'Code');
            const description = get(ppDef, 'Description');
            const calcPosition = percentage => {
              const fromSymbol = axis === 'y' ? 'UNP.' : 'VSP.';
              const toSymbol = axis === 'y' ? 'OVP.' : 'HSP.';
              const axDir = axis === 'y' ? -1 : 1;
              const from = symbols[fromSymbol];
              const to = symbols[toSymbol];
              const totalMullionLengths = batch.defs.reduce(
                (acc, def) => acc + getNumber(def, 'Width') / 1000,
                0
              );
              const mullionLengthsBefore = batch.defs
                .slice(0, defIndex)
                .reduce((acc, def) => acc + getNumber(def, 'Width') / 1000, 0);
              const totalGlassLength =
                to.absPos - from.absPos - totalMullionLengths * axDir;
              const glassLengthBefore = totalGlassLength * percentage;
              const mulWidth = getNumber(def, 'Width') / 1000;
              const mullionPos =
                from.absPos +
                from.width / 2 +
                (glassLengthBefore + mullionLengthsBefore * axDir) +
                (mulWidth / 2) * axDir;
              return mullionPos;
            };
            const setPosition = position => {
              const sourcePosition = getNumber(def, 'Position') / 1000;
              const sourceWidth = getNumber(def, 'Width') / 1000;
              for (const batch of batches.filter(
                x => x.defs[0].Orientation === def.Orientation
              )) {
                for (const profileDef of batch.defs) {
                  if (
                    getNumber(profileDef, 'Position') / 1000 !==
                    sourcePosition
                  ) {
                    continue;
                  }
                  if (getNumber(profileDef, 'Width') / 1000 !== sourceWidth) {
                    continue;
                  }
                  const mesh = this.movableProfiles.filter(
                    x => x.data.def === profileDef
                  );
                  if (mesh.length === 0) {
                    continue;
                  }
                  mesh[0].move(position);
                }
              }
            };
            mesh.addHelper(
              container,
              def,
              clearanceBounds,
              secondaryAxisPos,
              zPos,
              code,
              description,
              geo,
              calcPosition,
              setPosition
            );
          },
          def.ProfilePreferences
        );
      }
      mesh.onStateChanged();
    };
    mesh.onDeselect = () => {
      mesh.isSelected = false;
      this._renderer.interactionHandler.unhighlightAllObjects();
      mesh.removeHelpers();
      mesh.onStateChanged();
    };
    mesh.onHover = () => {
      mesh.isHovered = true;
      mesh.onStateChanged();
    };
    mesh.onUnhover = () => {
      mesh.isHovered = false;
      mesh.onStateChanged();
    };
    mesh.onHighlight = () => {
      mesh.isHighlighted = true;
      mesh.onStateChanged();
    };
    mesh.onUnhighlight = () => {
      mesh.isHighlighted = false;
      mesh.onStateChanged();
    };
    mesh.onStateChanged = () => {
      if (mesh.isSelected) {
        mesh.material = MULLION_SELECT_MATERIAL;
      } else if (mesh.isHovered) {
        mesh.material = MULLION_HOVER_MATERIAL;
      } else if (mesh.isHighlighted) {
        mesh.material = MULLION_HIGHLIGHT_MATERIAL;
      } else {
        mesh.material = meshMaterial;
      }
    };
  }

  if (def.SplitNumerator) {
    delete def.SplitNumerator;
  }
  if (def.SplitDenominator) {
    delete def.SplitDenominator;
  }

  mesh.buildEdges();
}

function buildSupplement(container, def, d) {
  if (true /*def.Supplement.TSupplementSpecification.Key === "Ventil"*/) {
    const innerTopBorder =
      getNumber(this.def, 'InnerMeasurements', 'OVP') / 1000;
    const outerTopBorder =
      getNumber(this.def, 'OuterMeasurements', 'OVP') / 1000;
    const supplementColor = getColorOrNull(
      def,
      'Supplement',
      'TSupplementSpecification',
      'Color'
    );

    const w = d.w * 0.5;
    const h = innerTopBorder * 0.5;
    const b = h;
    const geo = new THREE.BoxGeometry(w, h, b);
    const material =
      supplementColor === null
        ? d.treeMaterial
        : new THREE.MeshLambertMaterial({
            color: supplementColor,
          });
    const mesh = new THREE.Mesh(geo, material);
    mesh.position.y += d.h / 2 - h / 2 + outerTopBorder - innerTopBorder * 0.5;
    mesh.position.z -= b / 2 + d.b / 2;
    mesh.buildEdges();
    container.add(mesh);
  }
}

function buildSashInner(container, def, d) {
  if (this.isRectangular) {
    var symbols = {
      'OVP.': {
        name: 'OVP.',
        pos: d.h / 2,
        absPos: d.lastClearanceDimensions.top,
        axis: 'y',
        width: 0,
      },
      'UNP.': {
        name: 'UNP.',
        pos: -d.h / 2,
        absPos: d.lastClearanceDimensions.bottom,
        axis: 'y',
        width: 0,
      },
      'VSP.': {
        name: 'VSP.',
        pos: -d.w / 2,
        absPos: d.lastClearanceDimensions.left,
        axis: 'x',
        width: 0,
      },
      'HSP.': {
        name: 'HSP.',
        pos: d.w / 2,
        absPos: d.lastClearanceDimensions.right,
        axis: 'x',
        width: 0,
      },
    };
    d.symbols = symbols;
  }

  if (def.Profile) {
    var profileDef = get(def, 'Profile');
    const batches = prepareProfiles.call(this, profileDef, d);
    d.clearanceMullions = [];
    for (const batch of batches) {
      for (let i = 0; i < batch.defs.length; i++) {
        buildProfile.call(this, batches, batch, i, container, d);
        const mulWidth = getNumber(batch.defs[i], 'Width') / 1000;
        batch.widthLeft -= mulWidth;
      }
    }
    delete d.clearanceMullions;
  }
  if (def.SashClearance) {
    buildSashClearance.call(
      this,
      container,
      get(def, 'SashClearance', 'TSashClearance'),
      d
    );
  }
  if (def.Clearance) {
    util.forEachIfArray(
      this,
      buildSashClearance,
      get(def, 'Clearance'),
      container,
      d
    );
  }
  if (def.Supplement) {
    buildSupplement.call(this, container, def, d);
  }
  if (def.Accessories) {
    util.forEachIfArray(this, buildAccessories, def.Accessories, container, d);
  }
}

function adjustInnerAndOuterBorder(
  innerBorder,
  outerBorder,
  oldDepth,
  newOffset,
  newDepth
) {
  const backPoint = newOffset / oldDepth;
  const frontPoint = (newOffset + newDepth) / oldDepth;
  const newOuterBorder = sumObjects(
    outerBorder,
    multiplyObjectScalar(subtractObjects(innerBorder, outerBorder), backPoint)
  );
  const newInnerBorder = sumObjects(
    outerBorder,
    multiplyObjectScalar(subtractObjects(innerBorder, outerBorder), frontPoint)
  );
  return {
    innerBorder: newInnerBorder,
    outerBorder: newOuterBorder,
  };
}

function buildSash(container, def, d, isHighlighted) {
  const sashBreadth = d.b;
  const dBack = d.currClearanceOffset;
  const dFront = d.b - (d.currClearanceDepth + d.currClearanceOffset);
  buildMove(
    container,
    def,
    d,
    0,
    0,
    0,
    0,
    dFront,
    dBack,
    (container, def, d) => {
      var oldInnerBorderAdjust = Object.assign({}, d.innerBorderAdjust);
      var oldOuterBorderAdjust = Object.assign({}, d.outerBorderAdjust);
      var oldB = d.b;
      var oldW = d.w;
      var oldH = d.h;
      var oldTop = d.top;
      var oldBottom = d.bottom;
      var oldLeft = d.left;
      var oldRight = d.right;
      var innerBorder = getObjectNumbersInThousands(def, 'InnerMeasurements');
      var outerBorder = getObjectNumbersInThousands(def, 'OuterMeasurements');
      const newBorderMeasurements = adjustInnerAndOuterBorder(
        innerBorder,
        outerBorder,
        sashBreadth,
        d.currClearanceOffset,
        d.currClearanceDepth
      );
      innerBorder = newBorderMeasurements.innerBorder;
      outerBorder = newBorderMeasurements.outerBorder;

      d.sashBorderInnerWidth = innerBorder;

      {
        var depth = d.b;
        if (def.Depth) {
          depth = def.Depth / 1000;
        }
        d.b = depth;
      }

      if (
        Object.values(innerBorder).every(v => v === 0) &&
        Object.values(outerBorder).every(v => v === 0)
      ) {
        buildSashInner.call(this, container, def, d);
      } else {
        var sashGeo = this._createBorderGeometry(
          d.w,
          d.h,
          d.b,
          innerBorder,
          outerBorder,
          d.innerBorderAdjust,
          d.outerBorderAdjust,
          d.geometryData
        );
        var sashMesh = new THREE.Mesh(sashGeo, [
          d.treeMaterial,
          d.coupledFrameMaterial,
          d.whiteMaterial,
          MESH_HIGHLIGHT_MATERIAL,
        ]);
        if (d.coupledFrameMaterial) {
          setBackMaterial(sashMesh, 1);
        }
        setInsideMaterial(sashMesh, 2);

        sashMesh.buildEdges();

        container.add(sashMesh);

        if (isHighlighted) {
          const highlightedMesh = new THREE.Mesh(
            sashGeo,
            MESH_HIGHLIGHT_MATERIAL
          );
          container.add(highlightedMesh);
        }

        buildObject.call(
          this,
          container,
          def,
          d,
          innerBorder,
          outerBorder,
          buildSashInner
        );
      }

      d.left = oldLeft;
      d.right = oldRight;
      d.top = oldTop;
      d.bottom = oldBottom;
      d.w = oldW;
      d.h = oldH;
      d.b = oldB;

      d.innerBorderAdjust = oldInnerBorderAdjust;
      d.outerBorderAdjust = oldOuterBorderAdjust;
    }
  );
}

function buildHighlightedSpace(
  container,
  x,
  y,
  z,
  w,
  h,
  b,
  outerAdjust,
  geometryData
) {
  var vertices = this._create2DVertices(w, h, outerAdjust, geometryData);
  var geometry = createConvexSolid2DGeometry(vertices, false);
  var mesh = new THREE.Mesh(geometry, SPACE_HIGHLIGHT_MATERIAL);
  mesh.position.set(x, y, z);
  container.add(mesh);

  var mirrorGeometry = createConvexSolid2DGeometry(vertices, true);
  var meshMirror = new THREE.Mesh(mirrorGeometry, SPACE_HIGHLIGHT_MATERIAL);
  meshMirror.position.set(x, y, z);
  container.add(meshMirror);
}

function buildFrameClearance(container, def, d) {
  var oldW = d.w;
  var oldH = d.h;
  var oldB = d.b;
  var oldLeft = d.left;
  var oldRight = d.right;
  var oldTop = d.top;
  var oldBottom = d.bottom;
  var oldInnerBorderAdjust = Object.assign({}, d.innerBorderAdjust);
  var oldOuterBorderAdjust = Object.assign({}, d.outerBorderAdjust);

  d.frameClearanceW = d.w;
  d.frameClearanceH = d.h;
  d.frameClearanceB = d.b;
  buildSash.call(
    this,
    container,
    get(def, 'Sash', 'TSash'),
    d,
    getOrNull(def, 'IsHighlighted')
  );
  delete d.frameClearanceW;
  delete d.frameClearanceH;
  delete d.frameClearanceB;

  d.innerBorderAdjust = oldInnerBorderAdjust;
  d.outerBorderAdjust = oldOuterBorderAdjust;
  d.left = oldLeft;
  d.right = oldRight;
  d.top = oldTop;
  d.bottom = oldBottom;
  d.b = oldB;
  d.w = oldW;
  d.h = oldH;
}

function buildClearance(def, data) {
  def = get(def, 'TMDFSClearances');

  var layers = [];
  var back = getInt(def, 'CoupledIndex');
  if (back) {
    layers.push(back);
  }
  layers.push(getInt(def, 'Index'));

  if (this.isRectangular) {
    data.clearance.push({
      layers: layers,
      upperSymbol: get(def, 'Uppersymbol'),
      lowerSymbol: get(def, 'Lowersymbol'),
      leftSymbol: get(def, 'Leftsymbol'),
      rightSymbol: get(def, 'Rightsymbol'),
    });
  } else {
    data.clearance.push({
      layers: layers,
    });
  }
}

function buildFrame(container, def, d) {
  if (this.isRectangular) {
    var symbols = {
      'OVP.': {
        pos: d.h / 2,
        absPos: d.top,
        axis: 'y',
        width: 0,
      },
      'UNP.': {
        pos: -d.h / 2,
        absPos: d.bottom,
        axis: 'y',
        width: 0,
      },
      'VSP.': {
        pos: -d.w / 2,
        absPos: d.left,
        axis: 'x',
        width: 0,
      },
      'HSP.': {
        pos: d.w / 2,
        absPos: d.right,
        axis: 'x',
        width: 0,
      },
    };
    d.symbols = symbols;
  }

  if (def.Profile) {
    d.profileFrame = true;

    d.lastClearanceDimensions = {
      top: d.top,
      bottom: d.bottom,
      left: d.left,
      right: d.right,
    };

    var profileDefs = get(def, 'Profile');
    d.currProfiles = [];
    const batches = prepareProfiles.call(this, profileDefs, d);
    d.clearanceMullions = [];
    for (const batch of batches) {
      for (let i = 0; i < batch.defs.length; i++) {
        buildProfile.call(this, batches, batch, i, container, d);
        const mulWidth = getNumber(batch.defs[i], 'Width') / 1000;
        batch.widthLeft -= mulWidth;
      }
    }
    delete d.clearanceMullions;
    delete d.currProfiles;
    delete d.lastClearanceDimensions;

    d.profileFrame = false;
  }
  if (get(def, 'Clearance')) {
    d.clearance = new Array();
    var clearanceDefs = get(def, 'Clearance');
    util.forEachIfArray(this, buildClearance, clearanceDefs, d);
  }

  buildTFrame.call(this, container, get(def, 'TFrame'), d);
}

function buildAccessories(def, container, d) {
  if (def.Blind) {
    buildBlind.call(this, def.Blind, container, d);
  }
  if (def.Handle && d.layerIndex === d.currClearance.layers.length - 1) {
    buildHinging.call(this, def.Handle, container, d, () => {
      this._requireUpdate = true;
    });
  }
  if (def.Plisse) {
    buildPlisse.call(this, def.Plisse, container, d);
  }
  if (def.Mosquito) {
    buildMosquitoNet.call(this, def.Mosquito, container, d);
  }
}

function buildOpeningMarker(def, container, d) {
  const direction = getOrNull(def, 'Direction');
  let marker = undefined;
  switch (direction) {
    case 'F': {
      marker = createFixedFrameOpeningMarker(d.w, d.h);
      break;
    }
    case 'V': {
      marker = createTwistWindowOpeningMarker(d.w, d.h);
      break;
    }
    case 'VU': {
      marker = createLeftHungOutwardOpeningMarker(d.w, d.h);
      break;
    }
    case 'HU': {
      marker = createRightHungOutwardOpeningMarker(d.w, d.h);
      break;
    }
    case 'T': {
      marker = createTopHungOutwardOpeningMarker(d.w, d.h);
      break;
    }
    case 'B': {
      marker = createBottomHungInwardOpeningMarker(d.w, d.h);
      break;
    }
    case 'VI': {
      marker = createLeftHungInwardOpeningMarker(d.w, d.h);
      break;
    }
    case 'HI': {
      marker = createRightHungInwardOpeningMarker(d.w, d.h);
      break;
    }
    case 'VIKD': {
      marker = createLeftHungKippDrehInwardOpeningMarker(d.w, d.h);
      break;
    }
    case 'HIKD': {
      marker = createRightHungKippDrehInwardOpeningMarker(d.w, d.h);
      break;
    }
    case 'SDV': {
      marker = createPushLeftOpeningMarker(d.w, d.h);
      break;
    }
    case 'SDH': {
      marker = createPushRightOpeningMarker(d.w, d.h);
      break;
    }
  }
  if (marker) {
    marker.position.setZ(d.b / 2 + 1e-3);
    container.add(marker);
  }
}

function buildTFrame(container, def, d) {
  if (def.FrameClearance) {
    var oldW = d.w;
    var oldH = d.h;
    var oldB = d.b;

    d.totalClearanceDepth = undefined;
    d.currClearance = undefined;
    d.layerIndex = undefined;

    const clearanceOffsets = {};
    const clearanceDepths = {};

    for (var i1 = 0; i1 < d.clearance.length; i1++) {
      const clearance = d.clearance[i1];
      var oldIba = Object.assign({}, d.innerBorderAdjust);
      var oldOba = Object.assign({}, d.outerBorderAdjust);
      var depth = 0;
      for (var i2 = 0; i2 < d.clearance[i1].layers.length; i2++) {
        const clearanceIndex = d.clearance[i1].layers[i2];
        var clearanceDef = util
          .asArray(get(def, 'FrameClearance'))
          .find(function (def) {
            return getInt(def, 'TFrameClearance', 'Index') === clearanceIndex;
          });
        if (!clearanceDef) {
          throw 'Clearance with index ' + clearanceIndex + ' was not found';
        }

        let nextDepth = d.b;
        if (
          getExists(clearanceDef, 'TFrameClearance', 'Sash', 'TSash', 'Depth')
        ) {
          nextDepth =
            getNumber(
              clearanceDef,
              'TFrameClearance',
              'Sash',
              'TSash',
              'Depth'
            ) / 1000;
        }

        clearanceOffsets[clearanceIndex] = depth;
        clearanceDepths[clearanceIndex] = nextDepth;

        depth += nextDepth;
      }
      d.totalClearanceDepth = depth;
      d.currClearance = d.clearance[i1];
      d.currProfiles = [];

      if (d.clearance[i1].layers.length > 0) {
        const firstLayer = clearance.layers[0];
        const clearanceDef = util
          .asArray(get(def, 'FrameClearance'))
          .find(def => getInt(def, 'TFrameClearance', 'Index') === firstLayer);
        if (!clearanceDef) {
          throw 'Clearance with index ' + firstLayer + ' was not found';
        }
        const sashDef = get(clearanceDef, 'TFrameClearance', 'Sash', 'TSash');
        const outerMeasurements = getObjectNumbersInThousands(
          sashDef,
          'OuterMeasurements'
        );
        const outerSpaceMeasurements =
          getObjectNumbersInThousandsOrNull(
            sashDef,
            'OuterSpaceMeasurements'
          ) || d.frameOuterSpace;
        d.lastClearanceDimensions = {
          left: d.left,
          right: d.right,
          top: d.top,
          bottom: d.bottom,
        };
        if (this.isRectangular) {
          if (clearance.leftSymbol !== 'VSP.') {
            const sl = d.symbols[clearance.leftSymbol];
            d.lastClearanceDimensions.left = sl.absPos + sl.width / 2;
          }
          if (clearance.rightSymbol !== 'HSP.') {
            const sr = d.symbols[clearance.rightSymbol];
            d.lastClearanceDimensions.right = sr.absPos - sr.width / 2;
          }
          if (clearance.upperSymbol !== 'OVP.') {
            const st = d.symbols[clearance.upperSymbol];
            d.lastClearanceDimensions.top = st.absPos + st.width / 2;
          }
          if (clearance.lowerSymbol !== 'UNP.') {
            const sb = d.symbols[clearance.lowerSymbol];
            d.lastClearanceDimensions.bottom = sb.absPos - sb.width / 2;
          }
          d.lastClearanceDimensions.left +=
            (outerMeasurements.VSP || 0) + (outerSpaceMeasurements.VSP || 0);
          d.lastClearanceDimensions.right -=
            (outerMeasurements.HSP || 0) + (outerSpaceMeasurements.HSP || 0);
          d.lastClearanceDimensions.top +=
            (outerMeasurements.OVP || 0) + (outerSpaceMeasurements.OVP || 0);
          d.lastClearanceDimensions.bottom -=
            (outerMeasurements.UNP || 0) + (outerSpaceMeasurements.UNP || 0);
        }
      }

      for (var i2 = 0; i2 < d.clearance[i1].layers.length; i2++) {
        const oldInnerBorderAdjust = { ...d.innerBorderAdjust };
        var clearanceDef = util
          .asArray(get(def, 'FrameClearance'))
          .find(function (def) {
            return (
              getInt(def, 'TFrameClearance', 'Index') === clearance.layers[i2]
            );
          });
        if (!clearanceDef) {
          throw (
            'Clearance with index ' + clearance.layers[i2] + ' was not found'
          );
        }

        d.layerIndex = i2;

        const clearanceIndex = getNumber(
          clearanceDef,
          'TFrameClearance',
          'Index'
        );
        const clearanceOffset = clearanceOffsets[clearanceIndex] || 0;
        const clearanceDepth = clearanceDepths[clearanceIndex] || 0;
        var dLeft = 0,
          dRight = 0,
          dTop = 0,
          dBottom = 0;
        var dBack = 0;
        var dFront = d.b - d.totalClearanceDepth;
        if (this.isRectangular) {
          var sl = d.symbols[clearance.leftSymbol];
          var sr = d.symbols[clearance.rightSymbol];
          var st = d.symbols[clearance.upperSymbol];
          var sb = d.symbols[clearance.lowerSymbol];
          dLeft = sl.absPos + sl.width / 2 - d.left;
          dRight = d.right - sr.absPos + sr.width / 2;
          dTop = st.absPos + st.width / 2 - d.top;
          dBottom = d.bottom - sb.absPos + sb.width / 2;

          if (clearance.leftSymbol !== 'VSP.') {
            d.innerBorderAdjust.VSP = 0;
          }
          if (clearance.rightSymbol !== 'HSP.') {
            d.innerBorderAdjust.HSP = 0;
          }
        }

        d.currClearanceOffset = clearanceOffset;
        d.currClearanceDepth = clearanceDepth;

        buildMove.call(
          this,
          container,
          get(clearanceDef, 'TFrameClearance'),
          d,
          dLeft,
          dRight,
          dTop,
          dBottom,
          0,
          0,
          (container, def, d) => {
            if (def.Accessories) {
              util.forEachIfArray(
                this,
                buildAccessories,
                def.Accessories,
                container,
                d
              );
            }

            buildMove.call(
              this,
              container,
              def,
              d,
              0,
              0,
              0,
              0,
              dFront,
              dBack,
              (container, def, d) => {
                const isOutOfEditMode =
                  !this._renderer.interactionHandler ||
                  this._renderer.interactionHandler.interactionMode !==
                    InteractionMode.editMode;
                const showOpeningMarkers =
                  this._renderer.showOpeningMarkers === undefined ||
                  this._renderer.showOpeningMarkers;
                if (
                  showOpeningMarkers &&
                  isOutOfEditMode &&
                  this.isRectangular
                ) {
                  buildOpeningMarker(def, container, d);
                }

                const outerSpace =
                  getObjectNumbersInThousandsOrNull(
                    def,
                    'Sash',
                    'TSash',
                    'OuterSpaceMeasurements'
                  ) || d.frameOuterSpace;
                const innerSpace =
                  getObjectNumbersInThousandsOrNull(
                    def,
                    'Sash',
                    'TSash',
                    'InnerSpaceMeasurements'
                  ) || d.frameInnerSpace;
                buildObject.call(
                  this,
                  container,
                  def,
                  d,
                  innerSpace,
                  outerSpace,
                  buildFrameClearance
                );
              }
            );
          }
        );

        delete d.currClearanceOffset;
        delete d.currClearanceDepth;
        d.innerBorderAdjust = oldInnerBorderAdjust;
      }

      delete d.currProfiles;
      delete d.lastClearanceDimensions;
      d.innerBorderAdjust = oldIba;
      d.outerBorderAdjust = oldOba;
    }
    delete d.totalClearanceDepth;
    delete d.currClearance;
    delete d.layerIndex;

    d.b = oldB;
    d.w = oldW;
    d.h = oldH;
  }
  if (def.Supplement) {
    buildSupplement.call(this, container, def, d);
  }
  if (def.Accessories) {
    util.forEachIfArray(this, buildAccessories, def.Accessories, container, d);
  }
  if (def.Openable) {
    //TODO: In future version
  }
}

function buildWindowLabel(container, text, data) {
  const bottomMargin = 0.02;
  let backMesh, frontMesh;

  const rebuild = () => {
    if (backMesh) {
      container.remove(backMesh);
      disposeObject(backMesh);
    }
    if (frontMesh) {
      container.remove(frontMesh);
      disposeObject(frontMesh);
    }

    const camDist =
      this.parent.imageEngine && this.parent.imageEngine.camera
        ? this.parent.imageEngine.camera.getCameraDistance()
        : 1.5;
    const rendererHeightModifier =
      this.parent.imageEngine && this.parent.imageEngine.renderer
        ? 800 /
          this.parent.imageEngine.renderer.getSize(new THREE.Vector2()).height
        : 1;
    const fontSize = 30 * rendererHeightModifier * camDist;
    const meshData = createTextMesh(text, fontSize);

    // Back
    backMesh = meshData.mesh;
    backMesh.position.y = -this.height / 2 - bottomMargin - meshData.height / 2;
    backMesh.position.z = this.breadth / 2;
    container.add(backMesh);

    // Front
    frontMesh = backMesh.clone();
    frontMesh.position.z = -this.breadth / 2;
    frontMesh.rotateY(Math.PI);
    container.add(frontMesh);
  };
  rebuild();

  this.onCameraZoomEvents.push(() => {
    rebuild();
  });
  this.onRendererResizeEvents.push(() => {
    rebuild();
  });
}
function buildSideText(container, hasLabel) {
  const bottomMargin = hasLabel ? 0.05 : 0.02;
  let backMesh, frontMesh;
  const rebuild = () => {
    if (backMesh) {
      container.remove(backMesh);
      disposeObject(backMesh);
    }
    if (frontMesh) {
      container.remove(frontMesh);
      disposeObject(frontMesh);
    }

    const camDist =
      this.parent.imageEngine && this.parent.imageEngine.camera
        ? this.parent.imageEngine.camera.getCameraDistance()
        : 1.5;
    const rendererHeightModifier =
      this.parent.imageEngine && this.parent.imageEngine.renderer
        ? 800 /
          this.parent.imageEngine.renderer.getSize(new THREE.Vector2()).height
        : 1;
    const fontSize = 20 * rendererHeightModifier * camDist;
    const frontMeshData = createTextMesh('Insida', fontSize);
    const backMeshData = createTextMesh('Utsida', fontSize);

    // Utsida
    backMesh = backMeshData.mesh;
    backMesh.position.y =
      -this.height / 2 - bottomMargin - backMeshData.height / 2;
    backMesh.position.z = this.breadth / 2;
    container.add(backMesh);

    // Insida
    frontMesh = frontMeshData.mesh;
    frontMesh.position.y =
      -this.height / 2 - bottomMargin - frontMeshData.height / 2;
    frontMesh.position.z = -this.breadth / 2;
    frontMesh.rotateY(Math.PI);
    container.add(frontMesh);
  };
  rebuild();

  this.onCameraZoomEvents.push(() => {
    rebuild();
  });
  this.onRendererResizeEvents.push(() => {
    rebuild();
  });
}

function buildModel(showSideText) {
  var def = this.def;

  var width = getNumber(def, 'Width') / 1000;
  var height = getNumber(def, 'Height') / 1000;
  var breadth = getNumber(def, 'Depth') / 1000;

  this.width = width;
  this.height = height;
  this.breadth = breadth;

  var rootContainer = new THREE.Object3D();
  rootContainer.name = 'Window';
  rootContainer.data = {
    window: this,
  };
  this.obj = rootContainer;

  var container = new THREE.Object3D();
  container.position.x = width / 2;
  container.position.y = -height / 2;
  container.position.z = -breadth / 2;
  rootContainer.add(container);

  var data = {
    w: width, //Container width
    h: height, //Container height
    b: breadth, //Container breadth (aka depth)
    left: 0,
    right: width,
    top: 0,
    bottom: height,
    geometryData: this._parseGeometryData(getOrNull(def, 'Geometry')),
  };

  const innerBorder = getObjectNumbersInThousands(def, 'InnerMeasurements');
  const outerBorder = getObjectNumbersInThousands(def, 'OuterMeasurements');
  data.frameInnerSpace = getObjectNumbersInThousands(
    def,
    'InnerSpaceMeasurements'
  );
  data.frameOuterSpace = getObjectNumbersInThousands(
    def,
    'OuterSpaceMeasurements'
  );

  data.innerBorderAdjust = {};
  data.outerBorderAdjust = {};
  Object.keys(innerBorder).forEach(key => {
    data.innerBorderAdjust[key] = 0;
  });
  Object.keys(outerBorder).forEach(key => {
    data.outerBorderAdjust[key] = 0;
  });

  if (def.CoupledFrame) {
    buildCoupledFrame(get(def, 'CoupledFrame', 'TCoupledFrame'), data);
  }

  var frameGeo = this._createBorderGeometry(
    data.w,
    data.h,
    data.b,
    innerBorder,
    outerBorder,
    data.innerBorderAdjust,
    data.outerBorderAdjust,
    data.geometryData
  );
  //util.roundVertices(frameGeo);
  //frameGeo.mergeVertices();
  data.treeMaterial = new THREE.MeshLambertMaterial({
    color: USE_WIREFRAME
      ? 0
      : util.hexToNumber(
          get(def, 'Frame', 'TFrame', 'Coating', 'TCoatingSpecification', 'Key')
        ),
    wireframe: USE_WIREFRAME,
  });
  data.whiteMaterial = new THREE.MeshLambertMaterial({
    color: '#FFFFFF',
    wireframe: USE_WIREFRAME,
  });
  var frameMesh = new THREE.Mesh(frameGeo, [
    data.treeMaterial,
    data.coupledFrameMaterial,
    data.whiteMaterial,
  ]);
  if (data.coupledFrameMaterial) {
    setBackMaterial(frameMesh, 1);
  }

  if (!USE_WIREFRAME) {
    frameMesh.buildEdges();
  }

  this._onBuildModel(container, def, data);

  container.add(frameMesh);

  const isHighlighted = getBoolOrNull(def, 'Frame', 'TFrame', 'IsHighlighted');
  if (isHighlighted === true) {
    const highlightedMesh = new THREE.Mesh(frameGeo, MESH_HIGHLIGHT_MATERIAL);
    container.add(highlightedMesh);
  }

  buildObject.call(
    this,
    container,
    get(def, 'Frame'),
    data,
    innerBorder,
    outerBorder,
    buildFrame
  );

  if (def.Label) {
    buildWindowLabel.call(this, container, get(def, 'Label'), data);
  }
  if (showSideText) {
    buildSideText.call(this, container, getExists(def, 'Label'));
  }
}

export class Window {
  constructor(renderer, parent, def) {
    if (new.target === Window) {
      throw new TypeError('Cannot construct Window instance directly');
    }

    this._renderer = renderer;
    this._sashes = undefined;
    this._state = 0;
    this._flipFinalRotation = 0;
    this._flipSpeed = undefined;
    this._requireUpdate = false;
    this._initialMullionPositions = new Map();

    this.parent = parent;
    this.name = 'Window';
    this.obj = undefined; //Three.js Object3D
    this.def = def; //Json definition
    this.width = undefined;
    this.height = undefined;
    this.breadth = undefined;
    this.isOpen = false;
    this.id = undefined;
    this.measurement = undefined;
    this.interactableObjects = undefined;
    this.movableProfiles = undefined;
    this.allProfiles = undefined;
    this.isRectangular = this instanceof RectangularWindow;
    this.onRendererResizeEvents = [];
    this.onCameraZoomEvents = [];

    this._build();
  }

  _reset() {
    this._sashes = new Array();
    this.isOpen = false;
    this._state = 0;
    this._flipFinalRotation = 0;
    this.interactableObjects = [];
    this.movableProfiles = [];
    this.allProfiles = [];
    this.onRendererResizeEvents = [];
    this.onCameraZoomEvents = [];
  }

  _build() {
    this._reset();
    buildModel.call(this, this._renderer.showSideText);
  }

  _parseGeometryData(geometryData) {
    return null;
  }

  _setGeometryDataValue(name, value) {
    const propertyDef = get(this.def, 'Geometry', name);
    if (!propertyDef || !getBool(propertyDef, 'IsMovable')) {
      return;
    }

    propertyDef.Length = value;
  }

  _create2DVertices(width, height, adjust, geometryData) {
    throw 'Implementation missing';
  }

  _createSolidGeometry(
    width,
    height,
    breadth,
    innerAdjust,
    outerAdjust,
    geometryData
  ) {
    const vi = this._create2DVertices(width, height, innerAdjust, geometryData); // Front vertices
    const vo = this._create2DVertices(width, height, outerAdjust, geometryData); // Back vertices
    return createConvexSolidGeometry(vi, vo, breadth);
  }

  _createBorderGeometry(
    width,
    height,
    breadth,
    frontBorder,
    backBorder,
    frontAdjust,
    backAdjust,
    geometryData
  ) {
    const vfi = this._create2DVertices(
      width,
      height,
      subtractObjects(frontAdjust, frontBorder),
      geometryData
    ); // Front inner vertices
    const vfo = this._create2DVertices(
      width,
      height,
      frontAdjust,
      geometryData
    ); // Front outer vertices
    const vbi = this._create2DVertices(
      width,
      height,
      subtractObjects(backAdjust, backBorder),
      geometryData
    ); // Back inner vertices
    const vbo = this._create2DVertices(width, height, backAdjust, geometryData); // Back outer vertices
    return createConvexBorderGeometry(vfi, vfo, vbi, vbo, breadth);
  }

  _createMullionGeometry(
    width,
    height,
    breadth,
    innerAdjust,
    outerAdjust,
    geometryData
  ) {
    return this._createSolidGeometry(
      width,
      height,
      breadth,
      innerAdjust,
      outerAdjust,
      geometryData
    );
  }

  _transformDimensionsToRect(dimensions, width, height, geometryData) {
    return {
      left: dimensions.VSP,
      right: dimensions.HSP,
      top: dimensions.OVP,
      bottom: dimensions.UNP,
    };
  }

  _getGeometryChangeOnShrink(geometryData, width, height, rect) {
    return {};
  }

  _onBuildModel(container, def, data) {
    // Used in subclasses
  }

  rebuild() {
    if (this.obj) {
      this.parent.obj.remove(this.obj);
      disposeObject(this.obj);
    }

    this._build();
    if (this.measurement) {
      this.measurement.rebuild();
    }
    if (this.sideText) {
      this.sideText.rebuild();
    }
  }

  update(deltaTime) {
    var changed = false;
    if (this._state & STATE_OPENING) {
      changed = true;
      for (var i = 0; i < this._sashes.length; i++) {
        var sash = this._sashes[i];
        sash.rotation.y += deltaTime;
        if (sash.rotation.y >= Math.PI / 2) {
          sash.rotation.y = Math.PI / 2;
          this._state &= ~STATE_OPENING;
        }
      }
    } else if (this._state & STATE_CLOSING) {
      changed = true;
      for (var i = 0; i < this._sashes.length; i++) {
        var sash = this._sashes[i];
        sash.rotation.y -= deltaTime;
        if (sash.rotation.y <= 0) {
          sash.rotation.y = 0;
          this._state &= ~STATE_CLOSING;
        }
      }
    }

    if (this._state & STATE_FLIPPING) {
      changed = true;
      this.obj.rotation.y += deltaTime * this._flipSpeed;
      if (this.obj.rotation.y >= this._flipFinalRotation) {
        this.obj.rotation.y = this._flipFinalRotation % (Math.PI * 2);
        this._state &= ~STATE_FLIPPING;
      }
    }

    var shouldUpdate = changed || this._requireUpdate;
    this._requireUpdate = false;
    return shouldUpdate;
  }

  open() {
    if (this.obj) {
      this._state |= STATE_OPENING;
      this._state &= ~STATE_CLOSING;
      this.isOpen = true;
    }
  }

  close() {
    if (this.obj) {
      this._state |= STATE_CLOSING;
      this._state &= ~STATE_OPENING;
      this.isOpen = false;
    }
  }

  flip(speed) {
    if (speed === undefined) {
      speed = 1.0;
    }

    this._flipSpeed = speed;
    if (this.obj) {
      if (!(this._state & STATE_FLIPPING)) {
        this._state |= STATE_FLIPPING;
        this._flipFinalRotation = this.obj.rotation.y + Math.PI;
      }
    }
  }

  addMeasurement() {
    this.removeMeasurement();
    this.measurement = new Measurement(this);
    this.measurement.build();
    this.obj.add(this.measurement.obj);
  }

  removeMeasurement() {
    if (this.measurement) {
      this.obj.remove(this.measurement.obj);
      this.measurement.clear();
      this.measurement = undefined;
    }
  }

  rebuildMeasurement() {
    if (this.measurement) {
      this.removeMeasurement();
      this.addMeasurement();
    }
  }
  addSideText() {
    this.removeSideText();
    this.sideText = new SideText(this);
    this.sideText.build();
    this.obj.add(this.sideText.obj);
  }

  removeSideText() {
    if (this.sideText) {
      this.obj.remove(this.sideText.obj);
      this.sideText.clear();
      this.sideText = undefined;
    }
  }

  rebuildSideText() {
    if (this.sideText) {
      this.removeSideText();
      this.addSideText();
    }
  }

  add(obj) {
    this.obj.add(obj);
  }

  remove(obj) {
    this.obj.remove(obj);
  }

  addInteractableObject(obj) {
    this.interactableObjects.push(obj);
  }

  removeInteractableObject(obj) {
    this.interactableObjects = this.interactableObjects.filter(x => x !== obj);
  }

  onCameraZoom() {
    for (const event of this.onCameraZoomEvents) {
      event();
    }
    this.rebuildMeasurement();
    this.rebuildSideText();
  }

  onRendererResize() {
    for (const event of this.onRendererResizeEvents) {
      event();
    }
    this.rebuildMeasurement();
    this.rebuildSideText();
  }
}
